// components/features/calls/CallRow.tsx
import { useState, useEffect } from 'react';
import { Call } from "../../../types/features/Call";
import TableRow from "../../ui/TableRow";
import { callService } from '../../../services/callService';
import { AudioPlayer } from '../../ui/AudioPlayer';
import { Loader2 } from 'lucide-react';

interface CallRowProps {
  call: Call;
  onAction: (call: Call) => void;
  onSelect?: (call: Call, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function CallRow({
  call,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false
}: CallRowProps) {
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPlayer, setShowPlayer] = useState<boolean>(false);
  useEffect(() => {
    return () => {
      if (audioSrc) {
        URL.revokeObjectURL(audioSrc);
      }
    };
  }, [audioSrc]);

  const handleLoadAudio = async () => {
    if (audioSrc) {
      setShowPlayer(!showPlayer);
      return;
    }

    try {
      setIsLoading(true);
      const blobUrl = await callService.getCallAudioStream(call.id);
      setAudioSrc(blobUrl);
      setShowPlayer(true);
    } catch (error: any) {
      console.error("Erreur lors du chargement de l'audio", error.response);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'complété':
        return <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200 font-medium">Complété</span>;
      case 'no-answer':
      case 'non-répondu':
      case 'busy':
      case 'occupé':
      case 'failed':
      case 'manqué':
        return <span className="text-[10px] bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full border border-rose-200 font-medium">Manqué</span>;
      default:
        return status ? <span className="text-[10px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full border border-border">{status}</span> : null;
    }
  };

  const clientDisplayName = call.client?.firstname && call.client?.lastname
    ? `${call.client.firstname} ${call.client.lastname}`
    : call.client?.phone || call.client_phone || call.phone || 'Numéro inconnu';

  return (
    <TableRow
      item={call}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Contact & Direction */}
      <td className="py-3 px-4">
        <div>
          <span className="font-medium text-foreground block truncate">
            {clientDisplayName}
          </span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
            <span className={`inline-flex items-center font-medium ${call.direction === 'entrant' ? 'text-primary' : 'text-emerald-600'}`}>
              {call.direction === 'entrant' ? '↙ Entrant' : '↗ Sortant'}
            </span>
            {getStatusBadge(call.status)}
          </div>
        </div>
      </td>
       {/* Agent */}
      <td className="hidden sm:table-cell py-3 px-4">
        <span className="text-muted-foreground text-sm font-mono">
          {call.agent?.full_name}
        </span>
      </td>

      {/* Durée */}
      <td className="hidden sm:table-cell py-3 px-4">
        <span className="text-muted-foreground text-sm font-mono">
          {formatDuration(call.duration || 0)}
        </span>
      </td>

      {/* Enregistrement Audio (Service + AudioPlayer) */}
      <td className="hidden md:table-cell py-3 px-4">
        {call.recording_url ? (
          <div>
            {!showPlayer ? (
              <button
                onClick={handleLoadAudio}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium rounded-md transition border border-primary/20"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Chargement...</span>
                  </>
                ) : (
                  <span>▶ Écouter l'appel</span>
                )}
              </button>
            ) : (
              audioSrc && (
                <div className="w-64">
                  <AudioPlayer src={audioSrc} autoPlay={true} />
                </div>
              )
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">Aucun enregistrement</span>
        )}
      </td>

      {/* Date */}
      <td className="hidden lg:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-muted-foreground text-xs">
          {formatDate(call.created_at)}
        </span>
      </td>
    </TableRow>
  );
}